import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {NG_VALUE_ACCESSOR} from '@angular/forms';
import {NgClass, NgOptimizedImage} from '@angular/common';

@Component({
  selector: 'app-input-dropdown',
  templateUrl: './input-dropdown.component.html',
  styleUrls: ['./input-dropdown.component.css'],
  imports: [
    NgClass,
    NgOptimizedImage
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputDropdownComponent),
      multi: true
    }
  ]
})
export class InputDropdownComponent implements OnChanges {
  private static nextId = 0;

  @Input() title?: string;
  @Input() placeholder?: string;
  @Input() listaValori?: any[];
  @Input() unitaMisura?: string;
  @Input() defaultValue: any = undefined;
  @Input() size: string = 'm';
  @Input() w: '' | 'l' = '';
  @Input() disabled: boolean = false;
  @Input() disableInputOnly: boolean = false;
  @Input() alternative: boolean = false;
  @Input() required?: boolean = false;
  @Input() border?: 'grey' | '' = '';
  @Input() textAlign?: 'center' | 'left' | 'right' = 'center';
  @Input() marginTop?: 'mt8' | '' = '';
  @Input() padding?: 'laterale' | '' = '';

  @Output() valoreSelectedEmit: EventEmitter<any> = new EventEmitter<any>();
  // Emette lo stato di apertura/chiusura del dropdown (true = aperto, false = chiuso)
  @Output() expandedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('trigger', {static: false}) triggerEl?: ElementRef<HTMLElement>;
  @ViewChildren('optionEl') optionEls!: QueryList<ElementRef<HTMLElement>>;

  private onChange: any = () => {};
  private onTouched: any = () => {};

  valoreSelezionato: any = undefined;
  isExpanded: boolean = false;

  // A11y ids
  idBase: string = `input-dropdown-${++InputDropdownComponent.nextId}`;
  dropdownId: string = `${this.idBase}-listbox`;
  labelId: string = `${this.idBase}-label`;

  // Index attivo per navigazione tastiera
  activeIndex: number = -1;

  constructor(private ref: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['defaultValue']) {
      this.valoreSelezionato = this.defaultValue;
    }
  }

  get selectedIndex(): number {
    if (!this.listaValori || this.listaValori.length === 0) {
      return -1;
    }
    return this.listaValori.findIndex(v => v === this.valoreSelezionato);
  }

  optionId(i: number): string {
    return `${this.idBase}-option-${i}`;
  }

  toggleDropdown(): void {
    if (this.disabled) {
      return;
    }
    if (this.isExpanded) {
      this.closeDropdown(true);
    }
    else {
      this.openDropdown();
    }
  }

  private openDropdown(): void {
    if (this.disabled) {
      return;
    }
    this.isExpanded = true;
    this.expandedChange.emit(true);
    // inizializza l'indice attivo alla selezione corrente oppure al primo elemento
    const sel = this.selectedIndex;
    this.activeIndex = sel >= 0 ? sel : 0;
    this.ref.markForCheck();
    // sposta il focus sull'opzione attiva
    setTimeout(() => this.focusOption(this.activeIndex), 0);
  }

  private closeDropdown(focusTrigger: boolean = true): void {
    this.isExpanded = false;
    this.expandedChange.emit(false);
    this.activeIndex = -1;
    this.ref.markForCheck();
    if (focusTrigger && this.triggerEl?.nativeElement) {
      this.triggerEl.nativeElement.focus();
    }
  }

  /**
   * Permette al componente padre di chiudere forzatamente il dropdown
   * (ad esempio quando si apre il menu laterale su mobile).
   * Non sposta il focus sul trigger, per evitare "salti" indesiderati
   * quando l'utente sta interagendo con altri elementi.
   */
  closeFromOutside(): void {
    if (!this.isExpanded) {
      return;
    }
    this.closeDropdown(false);
  }

  private focusOption(index: number): void {
    if (!this.optionEls) {
      return;
    }
    const arr = this.optionEls.toArray();
    const el = arr[index]?.nativeElement;
    if (el) {
      el.focus();
    }
  }

  private setActive(index: number): void {
    const len = this.listaValori?.length ?? 0;
    if (len === 0 || index < 0 || index >= len) {
      return;
    }
    this.activeIndex = index;
    this.ref.markForCheck();
    setTimeout(() => this.focusOption(index), 0);
  }

  private moveActive(delta: number): void {
    const len = this.listaValori?.length ?? 0;
    if (len === 0) {
      return;
    }
    let idx = this.activeIndex;
    if (idx < 0) {
      idx = this.selectedIndex >= 0 ? this.selectedIndex : 0;
    }
    idx = (idx + delta + len) % len;
    this.setActive(idx);
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (this.isExpanded) {
          this.closeDropdown(true);
        }
        else {
          this.openDropdown();
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!this.isExpanded) {
          this.openDropdown();
        }
        else {
          this.moveActive(1);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!this.isExpanded) {
          this.openDropdown();
        }
        else {
          this.moveActive(-1);
        }
        break;
      case 'Escape':
        if (this.isExpanded) {
          event.preventDefault();
          this.closeDropdown(true);
        }
        break;
      default:
        break;
    }
  }

  onOptionKeydown(event: KeyboardEvent, index: number): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveActive(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActive(-1);
        break;
      case 'Home':
        event.preventDefault();
        this.setActive(0);
        break;
      case 'End':
        event.preventDefault();
        this.setActive((this.listaValori?.length ?? 1) - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        // seleziona l'elemento attivo (fallback all'indice passato)
        const idx = this.activeIndex >= 0 ? this.activeIndex : index;
        const val = this.listaValori?.[idx];
        if (val !== undefined) {
          this.sceltaValore(val);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.closeDropdown(true);
        break;
      case 'Tab':
        // lascia il comportamento di default, ma chiudi il menu
        this.closeDropdown(false);
        break;
      default:
        break;
    }
  }

  sceltaValore(valore: any): void {
    this.valoreSelezionato = valore;
    this.valoreSelectedEmit.emit(valore);
    this.onChange(valore);
    this.onTouched();
    this.closeDropdown(true);
  }

  writeValue(value: any): void {
    if (value !== undefined) {
      this.valoreSelezionato = value;
      this.ref.markForCheck();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.ref.markForCheck();
  }
}
